import React from 'react'

import HeaderMeta from 'components/HeaderMeta'
import Content, { Theme as ContentTheme } from 'components/base/Content'
import Title from 'components/base/Title'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Link from 'components/base/Link'
import ResponsiveImage from 'components/base/ResponsiveImage'
import ProgressiveImage from 'components/base/ProgressiveImage'
import StartQuoteButton from 'components/base/StartQuoteButton'

import FeaturedNews from 'components/blocks/FeaturedNews'
import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import Leadership from 'components/blocks/Leadership'
import Connect from 'components/blocks/Connect'

import InlInteriorBgr from 'assets/images/backgrounds/inl-interior.webp'
import InlInteriorBgr64 from 'assets/images/backgrounds/inl-interior-placeholder'

const OurStoryScreen: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Our Story | Oregon | Insurance Lounge'
      description='Learn how our no-commission agency can help you find the right policies for all your insurance needs, whether you prefer to get quotes online or in-person.'
      ogImg='/images/og-images/OurStory.webp'
    />
    <PageHero theme={PageHeroTheme.story}>Our Story</PageHero>
    <Content margined={true}>
      <Title level={2}>Simplifying Insurance</Title>
      <Paragraph theme={ParagraphTheme.introLarge}>
        Insurance Lounge is the culmination of an idea whose sole purpose is making insurance easier to purchase and understand for consumers.
        With a heavy emphasis on technology and simplicity, Insurance Lounge will pave a new way for consumers to interact online and in our storefronts by creating a truly interactive insurance experience.
      </Paragraph>
      <Content short={true} center={true} margined={true}>
        <Title level={3}>
          The Country’s First Interactive Retail Storefront
        </Title>
        <Paragraph>
          Our retail storefront creates an interactive atmosphere for consumers to learn about, shop for, and compare insurance policies by using multiple buying options.
          Our interactive product boxes give a visual and tangible quality to each insurance product. We can accommodate the mainstream insurance buyer with a traditional appointment, as well as the new age shopper who wants to do most of their research online through our in-store education and quote kiosks.
          This new hybrid approach allows Insurance Lounge to be the only retail storefront for insurance that can take care of any type of shopper.
        </Paragraph>
      </Content>
      <Content theme={ContentTheme.imageWrapper}>
        <ResponsiveImage>
          <ProgressiveImage
            placeholder={InlInteriorBgr64}
            src={InlInteriorBgr}
            src2x={undefined}
            alt='Insurance Lounge Interior'
          />
        </ResponsiveImage>
      </Content>
      <Content short={true} center={true} margined={true}>
        <Title level={3}>
          Insurance Concierge<sup>™</sup> Services
        </Title>
        <Paragraph>
          Creating an evenly matched experience for our online and phone customers also was a priority, so we introduced Insurance Concierge™ services.
          Our Insurance Concierge™ services include counseling, brokering, administrating, and
          underwriting services to our online customers for numerous insurance products, including auto, home, life, health, disability, long-term care, investment, and retirement products.
          Our Insurance Concierge™ services give our online customers one-on-one personal attention for any questions they may have. This service establishes high
          standards to make sure we meet your every need.
          Save your quote online, and we’ll share it with you via email. Plus, you can always return to our website to revise your quote or include additional policies.
        </Paragraph>
      </Content>
      <Content short={true} center={true}>
        <Title level={3}>One Source for All Your Insurance Needs</Title>
        <Paragraph>
          We’re your one-stop-shop for insurance. With access to quotes and rates from over 100 different insurance carriers across all lines of business,
          we can fulfill virtually every insurance need a consumer may have, from home, auto, life, and personal health insurance to commercial liability and group
          health insurance. We’re a no-commission agency, so there’s no pressure to buy or make a decision before you’re ready.
        </Paragraph>
        <Paragraph>
          <StartQuoteButton
            trackingInfo={{
              button_position: 'middle',
              page_category: 'about_us',
            }}
          >
            Quote & Buy a Policy Online
          </StartQuoteButton>
        </Paragraph>
      </Content>
      <Content short={true} center={true} margined={true}>
        <Title level={3}>Consumer Accessibility</Title>
        <Paragraph>
          Our <Link to='/locations/storefronts'>retail stores</Link> are open seven days a week, 9am-6pm on weekdays
          and 10am-5pm on weekends.
        </Paragraph>
      </Content>
    </Content>
    <Leadership />
    <FeaturedNews />
    <Connect
      trackingInfo={{
        button_position: 'bottom',
        page_category: 'about_us',
      }}
    />
  </div>
)

export default OurStoryScreen
